import React, { useState, useEffect, useRef, Component, Suspense, lazy, startTransition } from 'react';
import './styles/App.css';
import TopBar from './components/TopBar';
import TabBar from './components/TabBar';
import DashboardTab from './components/DashboardTab';
import { useDashboard } from './hooks/useNotionData';

// ── 코드 스플리팅 — 비활성 탭은 필요할 때만 로드 ──
const OfficeTab = lazy(() => import('./components/OfficeTab'));
const RevenueTab = lazy(() => import('./components/RevenueTab'));
const PlannerTab = lazy(() => import('./components/PlannerTab'));

// ── Error Boundary ─────────────────────────────────────────
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[CAIO] 렌더링 오류:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="n-error-boundary">
          <div className="n-error-boundary-title">오류가 발생했습니다</div>
          <div className="n-error-boundary-detail">{this.state.error?.message}</div>
          <button className="n-btn-ghost" onClick={() => this.setState({ hasError: false, error: null })}>
            다시 시도
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── 클라이언트 측 잠금 상태 헬퍼 ──────────────────────────
const LOCK_KEY = 'caio_auth_lock';
const MAX_FAILS = 5;
const LOCK_DURATION_MS = 5 * 60 * 1000; // 5분

function getLockState() {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (!raw) return { fails: 0, lockedUntil: 0 };
    return JSON.parse(raw);
  } catch { return { fails: 0, lockedUntil: 0 }; }
}

function saveLockState(state) {
  try { localStorage.setItem(LOCK_KEY, JSON.stringify(state)); } catch {}
}

function recordFailure() {
  const s = getLockState();
  const now = Date.now();
  const fails = (s.lockedUntil > now ? s.fails : s.fails) + 1;
  const lockedUntil = fails >= MAX_FAILS ? now + LOCK_DURATION_MS : s.lockedUntil;
  saveLockState({ fails, lockedUntil });
  return { fails, lockedUntil };
}

function resetLockState() {
  try { localStorage.removeItem(LOCK_KEY); } catch {}
}

// ── 비밀번호 게이트 ────────────────────────────────────────
function PasswordGate({ children }) {
  const [authed,   setAuthed]   = useState(() => sessionStorage.getItem('caio_auth') === '1');
  const [input,    setInput]    = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [lockInfo, setLockInfo] = useState(getLockState);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  // 잠금 해제 타이머
  useEffect(() => {
    if (lockInfo.lockedUntil > Date.now()) {
      timerRef.current = setInterval(() => {
        const s = getLockState();
        if (s.lockedUntil <= Date.now()) {
          resetLockState();
          setLockInfo({ fails: 0, lockedUntil: 0 });
          clearInterval(timerRef.current);
        } else {
          setLockInfo({ ...s });
        }
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [lockInfo.lockedUntil]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const remainingSec = Math.max(0, Math.ceil((lockInfo.lockedUntil - Date.now()) / 1000));
  const isLocked = lockInfo.lockedUntil > Date.now();

  const handleLogin = async () => {
    if (isLocked) return;
    if (!input.trim()) { setError('비밀번호를 입력하세요'); return; }
    setLoading(true); setError('');
    try {
      const res  = await fetch('/api/auth', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: input }),
      });
      const data = await res.json();
      if (data.ok) {
        resetLockState();
        sessionStorage.setItem('caio_auth', '1');
        setAuthed(true);
      } else {
        const newState = recordFailure();
        setLockInfo(newState);
        const left = MAX_FAILS - newState.fails;
        if (newState.lockedUntil > Date.now()) {
          setError(`비밀번호 오류 ${MAX_FAILS}회 초과 — 5분 후 재시도`);
        } else {
          setError(`비밀번호가 틀렸습니다 (${left > 0 ? `${left}회 남음` : ''})`);
        }
        setInput('');
        inputRef.current?.focus();
      }
    } catch {
      setError('서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  };

  if (authed) return children;

  return (
    <div className="pw-gate" role="main">
      <div className="pw-gate-card" role="dialog" aria-labelledby="pw-title" aria-describedby="pw-desc">
        <div className="pw-gate-logo" aria-hidden="true">CAIO</div>
        <div id="pw-title" className="pw-gate-title">대시보드 잠금</div>
        <div id="pw-desc" className="pw-gate-sub">
          {isLocked ? `너무 많이 시도했어요. ${remainingSec}초 후 다시 시도하세요.` : '비밀번호를 입력하세요'}
        </div>
        <input
          ref={inputRef}
          type="password"
          className="pw-gate-input"
          placeholder="비밀번호"
          value={input}
          onChange={e => { setInput(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && !loading && !isLocked && handleLogin()}
          disabled={loading || isLocked}
          aria-label="비밀번호"
          aria-invalid={!!error}
          aria-describedby={error ? 'pw-error' : undefined}
          autoComplete="current-password"
        />
        {error && (
          <div id="pw-error" className="pw-gate-error" role="alert" aria-live="assertive">
            {error}
          </div>
        )}
        <button
          className="pw-gate-btn"
          onClick={handleLogin}
          disabled={loading || isLocked}
          aria-busy={loading}
        >
          {loading ? '확인 중…' : isLocked ? `잠김 (${remainingSec}s)` : '입력'}
        </button>
      </div>
    </div>
  );
}

const VALID_TABS = ['dashboard', 'office', 'planner', 'revenue'];

function tabFromHash() {
  const h = window.location.hash.slice(1);
  return VALID_TABS.includes(h) ? h : 'dashboard';
}

// ── 메인 앱 ───────────────────────────────────────────────
export default function App() {
  const [tab, setTabState] = useState(() => tabFromHash());
  const [clock, setClock] = useState('');
  const [agentWorking, setAgentWorking] = useState(false);
  const [lastPlan, setLastPlan] = useState(null);
  const [lastChain, setLastChain] = useState(null);
  const [lastBrainstorm, setLastBrainstorm] = useState(null);

  const { data: notionData, loading: notionLoading, error: notionError, refresh: notionRefresh, silentRefresh: notionSilentRefresh } = useDashboard();

  // History API: 탭 전환 시 URL 해시 갱신 (뒤로가기 지원)
  const setTab = (newTab) => {
    startTransition(() => setTabState(newTab));
    window.history.pushState({ tab: newTab }, '', `#${newTab}`);
  };

  useEffect(() => {
    // 초기 해시 설정
    window.history.replaceState({ tab }, '', `#${tab}`);

    // 뒤로가기 / 앞으로가기
    const handlePop = (e) => {
      const t = e.state?.tab || tabFromHash();
      setTabState(t);
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = () => setClock(new Date().toTimeString().slice(0, 8));
    t(); const id = setInterval(t, 1000); return () => clearInterval(id);
  }, []);

  // 백그라운드 탭일 때 자동 새로고침 일시중지
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') notionRefresh?.();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [notionRefresh]);

  const pendingTodos = notionData?.todos?.pending || 0;

  const handleLogout = () => {
    sessionStorage.removeItem('caio_auth');
    window.location.reload();
  };

  return (
    <PasswordGate>
      <div className="caio-app">
        <TopBar clock={clock} notionData={notionData} onLogout={handleLogout} />
        <TabBar
          tab={tab}
          setTab={setTab}
          badges={{ office: pendingTodos, dashboard: 0, revenue: 0, planner: pendingTodos }}
        />
        <main className="tab-container" id={`panel-${tab}`} role="tabpanel" aria-live="polite">
          <ErrorBoundary key={tab}>
            <Suspense fallback={<div className="tab-content"><div className="n-skeleton-card"><div className="n-skeleton n-skeleton-line" /><div className="n-skeleton n-skeleton-line medium" /><div className="n-skeleton n-skeleton-line short" /></div></div>}>
              {tab === 'office' && (
                <OfficeTab
                  data={notionData} loading={notionLoading} error={notionError} onRefresh={notionRefresh}
                  agentWorking={agentWorking} setAgentWorking={setAgentWorking}
                  lastPlan={lastPlan} setLastPlan={setLastPlan}
                  lastChain={lastChain} setLastChain={setLastChain}
                  lastBrainstorm={lastBrainstorm} setLastBrainstorm={setLastBrainstorm}
                />
              )}
              {tab === 'dashboard' && (
                <DashboardTab
                  data={notionData} loading={notionLoading} error={notionError} onRefresh={notionRefresh}
                  onSilentRefresh={notionSilentRefresh}
                  onOfficeTab={() => setTab('office')}
                />
              )}
              {tab === 'revenue' && <RevenueTab />}
              {tab === 'planner' && (
                <PlannerTab data={notionData} loading={notionLoading} error={notionError} onRefresh={notionRefresh} />
              )}
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </PasswordGate>
  );
}
